import React from 'react';
import { Box, Typography, Button, Stack, Paper } from '@mui/material';
import { Refresh as RefreshIcon, Home as HomeIcon } from '@mui/icons-material';

// Top-level error boundary so an unexpected render error shows a recoverable
// UI instead of a blank white screen (UX-3 in docs/STATUS.md). Class component
// because React error boundaries require lifecycle methods.
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, info) {
        // Surface in the console for diagnostics; a future task can forward to a logger.
        console.error('Unhandled UI error:', error, info?.componentStack);
    }

    handleReset = () => {
        this.setState({ hasError: false, error: null });
    };

    render() {
        if (!this.state.hasError) return this.props.children;

        return (
            <Box
                sx={{
                    minHeight: '100vh',
                    backgroundColor: '#121212',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 3,
                }}
            >
                <Paper
                    sx={{
                        p: 4,
                        maxWidth: 480,
                        backgroundColor: 'rgba(255,255,255,0.04)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 2,
                        textAlign: 'center',
                    }}
                >
                    <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>
                        Something went wrong
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.55)', mt: 1 }}>
                        An unexpected error occurred while rendering this view. You can retry, or
                        return to the cockpit.
                    </Typography>
                    {this.state.error?.message && (
                        <Typography
                            variant="caption"
                            component="pre"
                            sx={{
                                display: 'block',
                                mt: 2,
                                p: 1.5,
                                textAlign: 'left',
                                whiteSpace: 'pre-wrap',
                                color: 'rgba(255,138,128,0.85)',
                                backgroundColor: 'rgba(0,0,0,0.3)',
                                borderRadius: 1,
                                fontFamily: 'monospace',
                            }}
                        >
                            {this.state.error.message}
                        </Typography>
                    )}
                    <Stack direction="row" spacing={1.5} sx={{ mt: 3, justifyContent: 'center' }}>
                        <Button variant="contained" size="small" startIcon={<RefreshIcon />} onClick={this.handleReset}>
                            Retry
                        </Button>
                        <Button variant="outlined" size="small" startIcon={<HomeIcon />} href="/">
                            Back to Cockpit
                        </Button>
                    </Stack>
                </Paper>
            </Box>
        );
    }
}

export default ErrorBoundary;
